import { RefObject, useCallback, useEffect, useRef } from "react";

import { MessageHelper } from "../../util/message-helper";
import { PresenterHelper } from "./presenter-helper";

interface ReportPresenterHeightProps {
    rootRef: RefObject<HTMLElement>;
    contentRef: RefObject<HTMLElement>;
    hasPresentation: boolean;
    mediaId: string;
    qplayerId: string;
}

/**
 * The slides layout has no height of its own, so a host that sizes the player from its width is
 * told the real height. Only the measurement is sent, and only when it changes, so the host's
 * resize cannot feed back.
 */
const useReportPresenterHeight = ({ rootRef, contentRef, hasPresentation, mediaId, qplayerId }: ReportPresenterHeightProps) => {
    const reportedHeightRef = useRef(0);

    const reportHeight = useCallback(() => {
        const root = rootRef.current;
        if (!root || !hasPresentation) {
            return;
        }
        const contentHeight = PresenterHelper.measureNaturalHeight(root, contentRef.current);
        if (!contentHeight || Math.abs(contentHeight - reportedHeightRef.current) <= 1) {
            return;
        }
        reportedHeightRef.current = contentHeight;
        MessageHelper.sendMessage({
            type: MessageHelper.MESSAGE_TYPES.presenterLayout,
            mediaId,
            metadata: { contentHeight },
            qplayerId,
        });
    }, [rootRef, contentRef, hasPresentation, mediaId, qplayerId]);

    useEffect(() => {
        const root = rootRef.current;
        if (!root || !hasPresentation) {
            return;
        }
        // Every row is watched: anything that changes the layout resizes one of them, because the
        // video/slide row absorbs whatever the others take.
        reportedHeightRef.current = 0;
        reportHeight();
        const observer = new ResizeObserver(() => reportHeight());
        observer.observe(root);
        Array.from(root.children).forEach((row) => observer.observe(row));
        return () => observer.disconnect();
    }, [rootRef, hasPresentation, reportHeight]);
};

export default useReportPresenterHeight;
